import {
    orderBy as _orderBy,
    partition as _partition,
} from 'lodash';

import { MarketAttribute } from '@core/constants';
import Market from '@core/models/offer/Market';
import OfferEvent from '@core/models/offer/OfferEvent';

type MarketPredicate = (market: MarketForArrange) => boolean;
type OrderByParam = MarketPredicate | string;
type OrderDirectionParam = 'desc' | 'asc';

export type MarketForArrange = Pick<
    Market,
    | 'id'
    | 'order'
    | 'odds'
    | 'uniqueId'
    | 'name'
    | 'isSuperbet'
    | 'isLocked'
    | 'isMain'
    | 'containsAttribute'
> & {
    includedSuperBetIds?: number[];
};

export function createSuperBetMarket(
    id: Market['id'],
    name: Market['name'],
    order: Market['order'],
    odds: Market['odds'],
    includedSuperBetIds?: number[],
): MarketForArrange {
    return {
        id,
        order,
        odds,
        uniqueId: `${id}`,
        name,
        isSuperbet: true,
        isLocked: false,
        isMain: false,
        includedSuperBetIds,
        containsAttribute: (): boolean => false,
    };
}

export function arrangeMarkets(
    markets: MarketForArrange[],
    primaryMarketAttribute: string,
    orderBy: OrderByParam[] = [],
    orderDirection: OrderDirectionParam[] = [],
): MarketForArrange[] {
    const partitionFn = primaryMarketAttribute === MarketAttribute.main
        ? (market: MarketForArrange) => market.isMain
        : (market: MarketForArrange) => market.containsAttribute(primaryMarketAttribute);

    const [primary, rest] = _partition(
        markets,
        partitionFn,
    );

    return primary.concat(
        _orderBy(rest, orderBy, orderDirection),
    );
}

function groupSuperbetMarkets(markets: MarketForArrange[], superbetsMarketName: string): MarketForArrange[] {
    const [superbets, other] = _partition(markets, (m) => m.isSuperbet);

    if (!superbets.length) {
        return other;
    }

    const id = superbets[0].id;
    const includedSuperBetIds = superbets.map((s) => s.id);
    const odds = superbets.map((s) => s.odds[0]);
    const order = Math.min(...odds.map((odd) => odd.marketOrder));

    const superbetsMarket = createSuperBetMarket(
        id,
        superbetsMarketName,
        order,
        odds,
        includedSuperBetIds,
    );

    return [...other, superbetsMarket];
}

export function getMarkets(
    event: OfferEvent,
    superbetsMarketName: string,
    primaryMarketAttribute: string,
    isFavouriteMarket: MarketPredicate,
): MarketForArrange[] {
    return arrangeMarkets(
        groupSuperbetMarkets(event.getMarkets(), superbetsMarketName),
        primaryMarketAttribute,
        [isFavouriteMarket, 'order'],
        ['desc', 'asc'],
    );
}
